import { useEffect, useRef, useState } from "react";
import JsBarcode from "jsbarcode";
import { Printer, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { genEan13 } from "@/lib/format";
import { BigButton, Chip } from "@/components/ui-kit";
import { printElement } from "@/lib/print";

export function Label({ p }) {
  const { money } = useApp();
  const ref = useRef(null);
  useEffect(() => {
    const opts = { height: 38, width: 1.5, fontSize: 12, margin: 0 };
    try { JsBarcode(ref.current, p.barcode, { ...opts, format: /^\d{13}$/.test(p.barcode) ? "EAN13" : "CODE128" }); } catch (e) { JsBarcode(ref.current, p.barcode, { ...opts, format: "CODE128" }); }
  }, [p.barcode]);
  return (
    <div className="border border-dashed border-[#0B2A5B]/40 rounded p-2 text-center bg-white" style={{ breakInside: "avoid" }} data-testid="barcode-label">
      <div className="text-xs font-bold truncate">{p.name}</div>
      <svg ref={ref} className="mx-auto max-w-full" />
      {p.extra && <div className="text-[11px]" dir="ltr">{p.extra}</div>}
      <div className="font-bold" data-testid="barcode-label-price">{money(p.price)}</div>
    </div>
  );
}

export default function LabelsDialog({ open, onClose, products }) {
  const { t, dispatch } = useApp();
  const sheet = useRef(null);
  const [copies, setCopies] = useState(1);
  const withCode = products.filter((p) => p.barcode);
  const without = products.filter((p) => !p.barcode);
  const generate = () => {
    without.forEach((p) => dispatch("product.upsert", { id: p.id, barcode: genEan13() }));
    toast.success(t("barcodes_generated", { n: without.length }));
  };
  const labels = withCode.flatMap((p) => Array.from({ length: copies }, (_, i) => ({ ...p, key: `${p.id}-${i}` })));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[94vh] overflow-y-auto" data-testid="labels-dialog">
        <DialogTitle className="font-heading text-2xl">{t("print_labels")}</DialogTitle>
        <div className="flex flex-wrap gap-2 items-center no-print">
          <span className="font-semibold">{t("copies")}:</span>
          {[1, 2, 4, 8].map((n) => <Chip key={n} active={copies === n} onClick={() => setCopies(n)} data-testid={`labels-copies-${n}`}>{n}</Chip>)}
        </div>
        {without.length > 0 && (
          <div className="rounded-xl bg-secondary p-3 flex flex-wrap items-center justify-between gap-2 no-print">
            <span className="text-sm font-semibold">{t("no_barcode_n", { n: without.length })}</span>
            <BigButton variant="soft" className="h-11 text-sm" onClick={generate} data-testid="labels-generate-btn"><Wand2 className="w-4 h-4" />{t("generate_barcodes")}</BigButton>
          </div>
        )}
        <div ref={sheet} className="print-area grid grid-cols-3 gap-2" style={{ width: "auto" }}>
          {labels.map((p) => <Label key={p.key} p={p} />)}
        </div>
        <BigButton disabled={!labels.length} onClick={() => printElement(sheet.current, { title: "etiquetas", brand: false })} className="w-full no-print" data-testid="labels-print-btn"><Printer className="w-5 h-5" />{t("print")} ({labels.length})</BigButton>
      </DialogContent>
    </Dialog>
  );
}
